// Phase 1 — dual-file (genotype + phenotype) upload + result panel.
//
// Niranjan (Feb 2026) — the first three panels of the Molecular tab
// (QTL / GWAS / GS) all need a *Phase 1: Data QC & Phenotypic
// Analysis* run as a precondition. This component is the dedicated
// UI for that step:
//
//   - Two file slots: numeric genotype matrix (0/1/2) and
//     phenotype table.
//   - "Try Sample Data" pulls a 100×500 synthetic genotype + a
//     100-line × 2-rep × 3-trait phenotype set with simulated H²
//     of approximately {0.75, 0.50, 0.25}.
//   - Tunable QC thresholds.
//   - Run → POST /api/molecular/phase1 → result panel.
//
// `Phase1ResultPanels` is exported separately so the
// /results/<id> page can re-use the same render for the
// `molecular_phase1` section.
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Loader2, CheckCircle2, ExternalLink, Download, FileText } from 'lucide-react';
import { API } from '@/App';
import { sha256OfFile, recordAnalysis, buildPhase1Summary } from '@/utils/molecularSessionRegistry';
import { StepHeader, ParamLabel, RuntimeEstimate, useCsvHeader, autoSelectColumn, estimateRuntime } from './uploadCardHelpers';

const TIPS = {
  genotype_col: "Column in the phenotype file that holds the genotype/line ID. Must match the row index of the genotype file.",
  rep_col: "Column for replication blocks. If provided, broad-sense H² is estimated via REML mixed model. Leave blank to use raw means.",
  maf_threshold: "Minor allele frequency cutoff. Markers below this MAF are dropped before heritability and correlation analysis.",
  missing_threshold: "Per-marker missingness ceiling. Markers with > this fraction of NaN genotype calls are dropped.",
  het_threshold: "Per-marker heterozygosity ceiling. Markers with > this fraction of heterozygous calls (excess het) are dropped.",
};


export function Phase1UploadCard({ onResult, analysisId, exporting, onExport }) {
  const [genoFile, setGenoFile] = useState(null);
  const [phenoFile, setPhenoFile] = useState(null);
  const [genotypeCol, setGenotypeCol] = useState('Genotype');
  const [repCol, setRepCol] = useState('Rep');
  const [mafThreshold, setMafThreshold] = useState(0.05);
  const [missingThreshold, setMissingThreshold] = useState(0.10);
  const [hetThreshold, setHetThreshold] = useState(0.50);
  const [running, setRunning] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [result, setResult] = useState(null);

  const onPickGeno = (e) => { const f = e.target.files?.[0]; if (f) setGenoFile(f); };
  const onPickPheno = (e) => { const f = e.target.files?.[0]; if (f) setPhenoFile(f); };

  const loadSampleData = async () => {
    setLoadingSample(true);
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API}/molecular/sample-data/genotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/sample-data/phenotype`, { responseType: 'blob', withCredentials: true }),
      ]);
      const gFile = new File([g.data], 'molecular_phase1_genotype.csv', { type: 'text/csv' });
      const pFile = new File([p.data], 'molecular_phase1_phenotype.csv', { type: 'text/csv' });
      setGenoFile(gFile);
      setPhenoFile(pFile);
      toast.success('Sample data loaded — 100 lines × 500 SNPs × 3 traits. Click Run.');
    } catch (e) {
      toast.error('Sample data fetch failed.');
    } finally { setLoadingSample(false); }
  };

  // V3 verification — Niranjan, Feb 2026: edge-case sample variants.
  const loadVariant = async (variant, label) => {
    setLoadingSample(true);
    try {
      const [g, p] = await Promise.all([
        axios.get(`${API}/molecular/sample-data-variant/${variant}/genotype`, { responseType: 'blob', withCredentials: true }),
        axios.get(`${API}/molecular/sample-data-variant/${variant}/phenotype`, { responseType: 'blob', withCredentials: true }),
      ]);
      setGenoFile(new File([g.data], `molecular_phase1_${variant}_genotype.csv`, { type: 'text/csv' }));
      setPhenoFile(new File([p.data], `molecular_phase1_${variant}_phenotype.csv`, { type: 'text/csv' }));
      toast.success(`Sample (${label}) loaded. Click Run.`);
    } catch (e) {
      toast.error(`Sample (${label}) fetch failed.`);
    } finally { setLoadingSample(false); }
  };

  const runAnalysis = async () => {
    if (!genoFile || !phenoFile) {
      toast.error('Both genotype and phenotype files are required.');
      return;
    }
    setRunning(true);
    setResult(null);
    const fd = new FormData();
    fd.append('genotype_file', genoFile);
    fd.append('phenotype_file', phenoFile);
    fd.append('genotype_col', genotypeCol);
    if (repCol) fd.append('rep_col', repCol);
    fd.append('maf_threshold', String(mafThreshold));
    fd.append('missing_threshold', String(missingThreshold));
    fd.append('het_threshold', String(hetThreshold));
    try {
      const { data } = await axios.post(`${API}/molecular/phase1`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      const r = data?.results?.molecular_phase1;
      setResult(r);
      onResult(data);
      // BATCH-NEXT-1 (Niranjan, Feb 2026): record this Phase 1 run
      // in the session registry so Phase 2/3/4 results pages can
      // surface the Cross-Phase Continuity Banner.
      try {
        const genotypeHash = await sha256OfFile(genoFile);
        recordAnalysis(data.id, {
          phase: 'phase1',
          genotypeHash,
          summary: { ...buildPhase1Summary(r), analysisId: data.id },
        });
      } catch { /* registry is best-effort */ }
      toast.success('Phase 1 complete!');
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Run failed';
      toast.error(`Phase 1 failed: ${msg}`);
    } finally { setRunning(false); }
  };

  const phenoHeaders = useCsvHeader(phenoFile);
  React.useEffect(() => {
    if (!phenoHeaders.length) return;
    if (!phenoHeaders.includes(genotypeCol)) {
      const guess = autoSelectColumn(phenoHeaders, ['^genotype$', '^line$', '^entry$', '^id$']);
      if (guess) setGenotypeCol(guess);
    }
    if (repCol && !phenoHeaders.includes(repCol)) {
      const guess = autoSelectColumn(phenoHeaders, ['^rep$', '^replication$', '^block$']);
      setRepCol(guess || '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phenoHeaders.join(',')]);

  return (
    <div className="space-y-5" data-testid="mol-phase1-card">
      {/* STEP 1 — UPLOAD */}
      <div>
        <StepHeader step="1" title="Upload your data" hint="Genotype + phenotype" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <FileSlot
            label="Genotype file (0/1/2 numeric matrix)"
            file={genoFile}
            onPick={onPickGeno}
            testid="mol-phase1-genotype-file"
            accept=".csv,.xlsx,.xls"
            hint="Rows = individuals; Cols = SNPs / markers; Values in {0, 1, 2, NaN}."
          />
          <FileSlot
            label="Phenotype file"
            file={phenoFile}
            onPick={onPickPheno}
            testid="mol-phase1-phenotype-file"
            accept=".csv,.xlsx,.xls"
            hint="Cols: Genotype | Rep (optional) | Trait1 | Trait2 …"
          />
        </div>
      </div>

      {/* STEP 2 — CONFIGURE */}
      <div>
        <StepHeader step="2" title="Configure analysis" />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <ColumnSelect label="Genotype column" value={genotypeCol} onChange={setGenotypeCol}
            options={phenoHeaders} testid="mol-phase1-genotype-col" hint={TIPS.genotype_col} />
          <ColumnSelect label="Rep column (optional)" value={repCol} onChange={setRepCol}
            options={phenoHeaders} allowNone testid="mol-phase1-rep-col" hint={TIPS.rep_col} />
          <NumInput label="MAF ≥" value={mafThreshold} setValue={setMafThreshold}
            step={0.01} min={0} max={0.5} testid="mol-phase1-maf-threshold" hint={TIPS.maf_threshold} />
          <NumInput label="Missingness ≤" value={missingThreshold} setValue={setMissingThreshold}
            step={0.05} min={0} max={1} testid="mol-phase1-missing-threshold" hint={TIPS.missing_threshold} />
          <NumInput label="Heterozygosity ≤" value={hetThreshold} setValue={setHetThreshold}
            step={0.05} min={0} max={1} testid="mol-phase1-het-threshold" hint={TIPS.het_threshold} />
        </div>
      </div>

      {/* STEP 3 — RUN */}
      <div>
        <StepHeader step="3" title="Run analysis" />
        <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={loadSampleData}
          disabled={loadingSample || running}
          className="flex items-center gap-1.5 text-xs font-semibold rounded-lg px-3.5 py-2 border border-[#0891B2]/30 text-[#0891B2] bg-[#0891B2]/5 hover:bg-[#0891B2]/10 disabled:opacity-50"
          data-testid="mol-phase1-sample-data"
        >
          {loadingSample ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Try Sample Data
        </button>
        <button
          type="button"
          onClick={() => loadVariant('single_trait', 'Single Trait')}
          disabled={loadingSample || running}
          className="flex items-center gap-1.5 text-[11px] font-medium rounded-lg px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#0891B2]/40 hover:text-[#0891B2] disabled:opacity-50"
          data-testid="mol-phase1-sample-single-trait"
        >
          <Download size={11} /> Sample (Single Trait)
        </button>
        <button
          type="button"
          onClick={() => loadVariant('missing25', 'Missing Heavy')}
          disabled={loadingSample || running}
          className="flex items-center gap-1.5 text-[11px] font-medium rounded-lg px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#0891B2]/40 hover:text-[#0891B2] disabled:opacity-50"
          data-testid="mol-phase1-sample-missing25"
        >
          <Download size={11} /> Sample (Missing Heavy)
        </button>
        <button
          type="button"
          onClick={() => loadVariant('n30', 'Small n=30')}
          disabled={loadingSample || running}
          className="flex items-center gap-1.5 text-[11px] font-medium rounded-lg px-3 py-2 border border-slate-200 text-slate-600 hover:border-[#0891B2]/40 hover:text-[#0891B2] disabled:opacity-50"
          data-testid="mol-phase1-sample-n30"
        >
          <Download size={11} /> Sample (Small n=30)
        </button>
        <button
          type="button"
          onClick={runAnalysis}
          disabled={running || !genoFile || !phenoFile}
          className="flex items-center gap-1.5 text-sm font-semibold rounded-lg px-4 py-2 bg-[#0891B2] text-white hover:bg-[#0E7490] disabled:opacity-50 shadow-sm"
          data-testid="mol-phase1-run"
        >
          {running ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {running ? 'Running…' : 'Run Phase 1 Analysis'}
        </button>
        <RuntimeEstimate testid="mol-phase1-runtime-estimate"
          estimate={estimateRuntime({ phase: 'phase1', nIndiv: 100, nMarkers: 500 })} />
        </div>
      </div>

      {result && (
        <div className="space-y-5 fade-in" data-testid="mol-phase1-result">
          <div
            className="flex items-center justify-between gap-3 rounded-xl px-4 py-3"
            style={{ background: '#0891B210', border: '1px solid #0891B230' }}
            data-testid="analysis-complete-banner"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center justify-center rounded-full shrink-0" style={{ width: 28, height: 28, background: '#0891B2', color: 'white' }}>
                <CheckCircle2 size={16} />
              </div>
              <div className="min-w-0">
                <div className="truncate" style={{ fontSize: 13, fontWeight: 700, color: '#0891B2' }}>
                  Phase 1 complete
                </div>
                <div className="truncate" style={{ fontSize: 11, color: '#5E5A56' }}>
                  Open the dedicated Results page for full tables, charts, and downloads.
                </div>
              </div>
            </div>
            {analysisId && (
              <button
                onClick={() => window.location.href = `/results/${analysisId}`}
                className="flex items-center gap-1.5 shrink-0 transition-all"
                style={{
                  fontSize: 13, fontWeight: 700, borderRadius: 8,
                  padding: '8px 18px', border: '1px solid #0891B2',
                  color: 'white', background: '#0891B2',
                  boxShadow: '0 2px 6px #0891B240',
                }}
                data-testid="view-full-results"
              >
                <ExternalLink size={13} /> View Full Results
              </button>
            )}
          </div>

          <Phase1ResultPanels result={result} />

          {analysisId && onExport && (
            <div className="flex items-center gap-2 flex-wrap pt-2" data-testid="export-bar">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mr-1">Export</span>
              {['docx', 'xlsx', 'pdf'].map(fmt => (
                <button
                  key={fmt}
                  onClick={() => onExport(analysisId, fmt)}
                  disabled={!!exporting}
                  className="flex items-center gap-1.5 text-[11px] font-semibold rounded-full px-3 py-1.5 transition-all border shadow-sm disabled:opacity-50 border-[#0891B2]/30 text-[#0891B2] bg-[#0891B2]/5 hover:bg-[#0891B2]/10"
                  data-testid={`export-${fmt}-button`}
                >
                  {exporting === `${analysisId}-${fmt}` ? (
                    <Loader2 size={11} className="animate-spin" />
                  ) : (
                    <Download size={11} />
                  )}
                  {fmt.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function FileSlot({ label, file, onPick, testid, accept, hint }) {
  return (
    <label className="block rounded-xl border-2 border-dashed border-[#E8E6E1] hover:border-[#0891B2] p-4 cursor-pointer transition-colors bg-white" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <FileText size={14} className="text-[#0891B2]" />
        <span className="text-xs font-semibold text-slate-700">{label}</span>
      </div>
      <input type="file" accept={accept} onChange={onPick} className="hidden" />
      <div className="text-xs text-slate-500 mb-2">{hint}</div>
      <div className="flex items-center gap-2 mt-2">
        {file ? (
          <span className="flex items-center gap-1.5 text-[11px] px-2 py-1 rounded bg-[#0891B2]/10 text-[#0891B2] font-mono truncate max-w-full">
            <CheckCircle2 size={11} />
            {file.name} ({(file.size / 1024).toFixed(1)} KB)
          </span>
        ) : (
          <span className="text-[11px] text-slate-400 italic">Click to select a CSV / XLSX file</span>
        )}
      </div>
    </label>
  );
}


function ColInput({ label, value, onChange, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input
        id={testid}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0891B2]/30"
        data-testid={testid}
      />
    </div>
  );
}


function ColumnSelect({ label, value, onChange, options, allowNone, testid, hint }) {
  if (!options || options.length === 0) {
    return <ColInput label={label} value={value} onChange={onChange} testid={testid} hint={hint} />;
  }
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <select id={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0891B2]/30 bg-white"
        data-testid={testid}>
        {allowNone && <option value="">(none)</option>}
        {options.map(o => <option key={o} value={o}>{o}</option>)}
        {value && !options.includes(value) && (
          <option value={value}>{value} (custom)</option>
        )}
      </select>
    </div>
  );
}


function NumInput({ label, value, setValue, step, min, max, testid, hint }) {
  return (
    <div className="block">
      <ParamLabel label={label} hint={hint} testid={testid} htmlFor={testid} />
      <input
        id={testid}
        type="number"
        value={value}
        onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
        step={step} min={min} max={max}
        className="w-full text-sm border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0891B2]/30"
        data-testid={testid}
      />
    </div>
  );
}


// Exported so /results/<id> can render the same panels for the
// `molecular_phase1` section instead of falling through to the
// generic key-value renderer (which would dump base64 as text).
export function Phase1ResultPanels({ result }) {
  const summary = result?.input_summary || {};
  const qc = result?.genotype_qc?.summary || {};
  if (!result) return null;
  return (
    <div className="space-y-5">
      {result.interpretation && (
        <div className="rounded-xl border border-[#0891B2]/30 bg-[#F0FBFD] px-4 py-3 text-sm text-slate-700 leading-relaxed" data-testid="mol-phase1-interpretation">
          {result.interpretation}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SummaryCard title="Input" testid="mol-phase1-input-summary" rows={[
          ['Individuals (genotype rows)', summary.n_genotype_individuals],
          ['Markers input', summary.n_genotype_markers_input],
          ['Markers retained after QC', summary.n_genotype_markers_retained],
          ['Phenotype observations', summary.n_phenotype_observations],
          ['Trait columns', (summary.trait_cols || []).join(', ') || '—'],
          ['Rep column', summary.rep_col || '—'],
        ]} />
        <SummaryCard title="QC drop counts" testid="mol-phase1-qc-summary" rows={[
          ['Filtered: MAF too low', qc.n_filtered_maf],
          ['Filtered: missingness too high', qc.n_filtered_missing],
          ['Filtered: heterozygosity too high', qc.n_filtered_het],
          ['MAF ≥', qc.thresholds?.maf_min],
          ['Missingness ≤', qc.thresholds?.missingness_max],
          ['Heterozygosity ≤', qc.thresholds?.heterozygosity_max],
        ]} />
      </div>

      {result.heritability && Object.keys(result.heritability).length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="mol-phase1-heritability-table">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
            Broad-sense Heritability (H²)
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50/50 text-slate-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">Trait</th>
                <th className="text-right px-4 py-2">σ²ɢ</th>
                <th className="text-right px-4 py-2">σ²ₑ</th>
                <th className="text-right px-4 py-2">H² (single plot)</th>
                <th className="text-right px-4 py-2">H² (mean basis)</th>
                <th className="text-left px-4 py-2">Category</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(result.heritability).map(([trait, h]) => (
                <tr key={trait} className="border-t border-slate-100">
                  <td className="px-4 py-2 font-mono text-xs">{trait}</td>
                  {h.error ? (
                    <td colSpan={5} className="px-4 py-2 text-amber-700 text-xs italic">{h.error}</td>
                  ) : (
                    <>
                      <td className="px-4 py-2 text-right tabular-nums">{h.sigma2_genotype}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{h.sigma2_error}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-semibold text-[#0891B2]">{h.broad_sense_h2}</td>
                      <td className="px-4 py-2 text-right tabular-nums">{h.h2_mean_basis}</td>
                      <td className="px-4 py-2 text-xs">{h.category}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.phenotype_distribution?.chart && (
        <ChartCard title="Phenotype distribution (histograms + boxplots)" b64={result.phenotype_distribution.chart} testid="mol-phase1-phenotype-chart" />
      )}

      {result.correlation?.chart && (
        <ChartCard title="Phenotype correlation heatmap (Pearson)" b64={result.correlation.chart} testid="mol-phase1-correlation-chart" />
      )}
    </div>
  );
}


function SummaryCard({ title, rows, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <dl className="divide-y divide-slate-100">
        {rows.map(([k, v], i) => (
          <div key={i} className="px-4 py-1.5 grid grid-cols-2 text-sm">
            <dt className="text-slate-500 text-xs">{k}</dt>
            <dd className="text-right text-slate-800 tabular-nums font-mono text-xs">{v ?? '—'}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function ChartCard({ title, b64, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold uppercase tracking-wider text-slate-600">
        {title}
      </div>
      <div className="p-4 flex justify-center">
        <img src={`data:image/png;base64,${b64}`} alt={title} className="max-w-full" />
      </div>
    </div>
  );
}
